import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Quaternion, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { BoundingBox } from "../core/mdl/BoundingBox";
import { projectPointOnPlane, quaternionFromAxesToRef, signedAngleBetweenVectors2, signedAngleBetweenVectors3, VECTOR2_X } from "../core/utils/MathUtils";
import { getLocalMatrixToRef } from "../core/utils/MeshUtils";
import { Camera } from "../core/view/Camera";
import { CameraMode } from "../core/view/CameraControl";
import { CoordinatesType } from "../core/view/View";
import { eq, radToDeg } from "../utils/math";
import { toLocaleLengthString } from "../utils/number";
export class BoundingBoxDimensions {
    constructor(pElements, pManager, pOptions) {
        this.onResetBounds = () => {
            this.mBoxPoints = null;
            this.mMngr.app.viewer.requestRenderFrame();
        };
        this.onBeforeCameraRender = (pCamera) => {
            if (pCamera instanceof Camera)
                this.updateDimensions(pCamera.view);
        };
        this.mMngr = pManager;
        this.mElements = pElements;
        this.mOptions = {
            color: "#595959",
            scaleFactor: 1,
            offsetFactor: 1
        };
        Object.assign(this.mOptions, pOptions);
        this.mBoxPoints = null;
        this.mBoxCenter = undefined;
        this.mDimensionX = this.prepareDimension();
        this.mDimensionY = this.prepareDimension();
        this.mDimensionZ = this.prepareDimension();
        this.mMngr.app.model.scene.onBeforeCameraRenderObservable.add(this.onBeforeCameraRender);
        for (const tElement of this.mElements) {
            tElement.eventBoundingBoxChanged.addListener(this.onResetBounds);
            tElement.eventTransformationChanged.addListener(this.onResetBounds);
        }
        this.mMngr.app.viewer.requestRenderFrame();
    }
    dispose() {
        this.disposeDimension(this.mDimensionX);
        this.disposeDimension(this.mDimensionY);
        this.disposeDimension(this.mDimensionZ);
        this.mMngr.app.model.scene.onBeforeCameraRenderObservable.removeCallback(this.onBeforeCameraRender);
        for (const tElement of this.mElements) {
            tElement.eventBoundingBoxChanged.removeListener(this.onResetBounds);
            tElement.eventTransformationChanged.removeListener(this.onResetBounds);
        }
        this.mMngr.app.viewer.requestRenderFrame();
    }
    prepareDimension() {
        const tScene = this.mMngr.app.model.scene;
        const tDim = {};
        //text texture
        tDim.textTexture = new DynamicTexture("dimension_textTexture", { width: 256, height: 64 }, tScene, false);
        tDim.textTexture.hasAlpha = true;
        //text material
        tDim.textMaterial = new PBRMaterial("dimension_textMaterial", tScene);
        tDim.textMaterial.albedoColor = Color3.White();
        tDim.textMaterial.disableLighting = true;
        tDim.textMaterial.unlit = true;
        tDim.textMaterial.albedoTexture = tDim.textTexture;
        tDim.textMaterial.backFaceCulling = false;
        tDim.textMaterial.freeze();
        //text node and plane
        tDim.textNode = new TransformNode("dimension_textNode", tScene);
        tDim.textNode.rotationQuaternion = new Quaternion();
        tDim.textPlane = CreatePlane("dimension_textPlane", { width: 1.0, height: 0.25 }, tScene);
        tDim.textPlane.material = tDim.textMaterial;
        tDim.textPlane.parent = tDim.textNode;
        tDim.textPlane.position.y = 0.125;
        tDim.textPlane.isPickable = false;
        tDim.textPlane.renderingGroupId = 1;
        tDim.textPlane.alwaysSelectAsActiveMesh = true;
        //line
        tDim.lines = CreateLineSystem("dimension_line", {
            lines: [
                [Vector3.Zero(), Vector3.Zero()],
                [Vector3.Zero(), Vector3.Zero()],
                [Vector3.Zero(), Vector3.Zero()] //offset line 2
            ],
            updatable: true,
            instance: null,
            useVertexAlpha: false
        }, tScene);
        tDim.lines.color = Color3.FromHexString(this.mOptions.color);
        tDim.lines.isPickable = false;
        tDim.lines.renderingGroupId = 1;
        tDim.lines.alwaysSelectAsActiveMesh = true;
        return (tDim);
    }
    disposeDimension(pDim) {
        pDim.textMaterial.dispose();
        pDim.textTexture.dispose();
        pDim.textNode.dispose();
        pDim.lines.dispose();
    }
    updateDimensions(pView) {
        this.updateBoundingBox();
        //get screen scale factor
        const tDist = pView.projectedDistance(this.mBoxCenter, this.mBoxCenter.add(pView.cameraControl.right.scale(0.1)));
        let tScaleFactor = tDist != null ? 1.0 / tDist : 0.1;
        tScaleFactor *= this.mOptions.scaleFactor;
        tScaleFactor *= pView.pixelScaleFactor;
        //camera position
        let tCamPosition = pView.camera.position;
        if (pView.cameraControl.cameraMode !== CameraMode.Perspective) {
            const tBoxSize = this.mBoxPoints[5].subtract(this.mBoxPoints[0]).length();
            const tDir = pView.cameraControl.direction.negate();
            tDir.scaleInPlace(tBoxSize * 2);
            tCamPosition = this.mBoxCenter.add(tDir);
        }
        this.updateDimension(pView, this.mDimensionX, tCamPosition, tScaleFactor);
        this.updateDimension(pView, this.mDimensionY, tCamPosition, tScaleFactor);
        this.updateDimension(pView, this.mDimensionZ, tCamPosition, tScaleFactor);
    }
    updateBoundingBox() {
        //already up to date?
        if (this.mBoxPoints != null)
            return;
        if (this.mElements.length === 1) {
            const tElement = this.mElements[0];
            this.mBoxPoints = tElement.boundingBoxVisible.getPoints(tElement.worldTransform);
            this.mBoxCenter = tElement.boundingBoxVisible.getCenter(tElement.worldTransform);
        }
        else {
            const tBBox = BoundingBox.FromElements(this.mElements, true, true);
            this.mBoxPoints = tBBox.getPoints();
            this.mBoxCenter = tBBox.getCenter();
        }
        //update length text
        this.updateDimensionLength(this.mDimensionX, Vector3.Distance(this.mBoxPoints[0], this.mBoxPoints[3]));
        this.updateDimensionLength(this.mDimensionY, Vector3.Distance(this.mBoxPoints[0], this.mBoxPoints[1]));
        this.updateDimensionLength(this.mDimensionZ, Vector3.Distance(this.mBoxPoints[0], this.mBoxPoints[7]));
        //setup X dimension
        let tEdge1 = this.buildBoxEdge(0, 3);
        let tEdge2 = this.buildBoxEdge(1, 2);
        let tEdge3 = this.buildBoxEdge(5, 6);
        let tEdge4 = this.buildBoxEdge(4, 7);
        this.mDimensionX.boxSides = [
            this.buildBoxSide(tEdge1, tEdge2, false, true),
            this.buildBoxSide(tEdge4, tEdge3, false, true),
            this.buildBoxSide(tEdge1, tEdge4, true)
        ];
        //setup Y dimension
        tEdge1 = this.buildBoxEdge(0, 1);
        tEdge2 = this.buildBoxEdge(2, 3);
        tEdge3 = this.buildBoxEdge(4, 5);
        tEdge4 = this.buildBoxEdge(6, 7);
        this.mDimensionY.boxSides = [
            this.buildBoxSide(tEdge1, tEdge2),
            this.buildBoxSide(tEdge3, tEdge4),
            this.buildBoxSide(tEdge2, tEdge3),
            this.buildBoxSide(tEdge1, tEdge4)
        ];
        //setup Z dimension
        tEdge1 = this.buildBoxEdge(0, 7);
        tEdge2 = this.buildBoxEdge(3, 4);
        tEdge3 = this.buildBoxEdge(2, 5);
        tEdge4 = this.buildBoxEdge(1, 6);
        this.mDimensionZ.boxSides = [
            this.buildBoxSide(tEdge1, tEdge2, true),
            this.buildBoxSide(tEdge2, tEdge3, false, true),
            this.buildBoxSide(tEdge1, tEdge4, false, true)
        ];
    }
    updateDimensionLength(pDim, pLength) {
        const tLengthStr = toLocaleLengthString(pLength, this.mMngr.app.i18n.locale, this.mMngr.app.i18n.lengthUnit, true);
        if (tLengthStr == null)
            return;
        const tSize = pDim.textTexture.getSize();
        pDim.textTexture.getContext().clearRect(0, 0, tSize.width, tSize.height);
        pDim.textTexture.drawText(tLengthStr, null, 48, "48px " + this.mMngr.app.theme.fontFamily, this.mOptions.color, "transparent");
    }
    buildBoxEdge(pPointIdx1, pPointIdx2) {
        const tEdge = {};
        tEdge.p1 = this.mBoxPoints[pPointIdx1];
        tEdge.p2 = this.mBoxPoints[pPointIdx2];
        tEdge.center = Vector3.Center(tEdge.p1, tEdge.p2);
        return (tEdge);
    }
    buildBoxSide(pEdge1, pEdge2, pIsBottomSide = false, pIgnoreE2 = false) {
        const tSide = {};
        tSide.e1 = pEdge1;
        tSide.e2 = pEdge2;
        tSide.ignoreE2 = pIgnoreE2;
        tSide.isBottomSide = pIsBottomSide;
        tSide.center = Vector3.Center(pEdge1.center, pEdge2.center);
        tSide.normal = tSide.center.subtract(this.mBoxCenter).normalize();
        return (tSide);
    }
    getCameraDirectionToPoint(pView, pPoint) {
        if (pView.cameraControl.cameraMode === CameraMode.Perspective) {
            return (pPoint.subtract(pView.camera.position).normalize());
        }
        else {
            return (pView.cameraControl.direction);
        }
    }
    updateDimension(pView, pDim, pCamPosition, pScaleFactor) {
        pDim.textNode.setEnabled(true);
        pDim.lines.setEnabled(true);
        //find side which is facing the camera
        let tFoundDirDot = 0;
        let tFoundSide = null;
        for (const tSide of pDim.boxSides) {
            const tCamDir = this.getCameraDirectionToPoint(pView, tSide.center);
            let tDirDot = Vector3.Dot(tCamDir, tSide.normal);
            if (tSide.isBottomSide) {
                if (tDirDot > 0)
                    tDirDot = -tDirDot;
                if (tDirDot < -0.05)
                    tDirDot = -1.0;
            }
            if (tFoundSide == null || tDirDot < tFoundDirDot) {
                tFoundDirDot = tDirDot;
                tFoundSide = tSide;
            }
        }
        const tSide = tFoundSide;
        //get edge
        let tEdge = tSide.e1;
        if (!tSide.ignoreE2) {
            const tEdge1Dist = Vector3.Distance(tSide.e1.center, pCamPosition);
            const tEdge2Dist = Vector3.Distance(tSide.e2.center, pCamPosition);
            if (eq(tEdge1Dist, tEdge2Dist)) {
                //if equal, choose edge with is located more at the lower-right (in screen space)
                const tE1Proj = projectPointOnPlane(tSide.e1.center, pView.cameraControl.viewPlane);
                const tBoxProj = projectPointOnPlane(this.mBoxCenter, pView.cameraControl.viewPlane);
                const tDirE1 = tE1Proj.subtract(tBoxProj).normalize();
                let tAngle = signedAngleBetweenVectors3(tDirE1, pView.cameraControl.right, pView.cameraControl.direction);
                tAngle = radToDeg(tAngle);
                if (tAngle < -135 || tAngle > 45)
                    tEdge = tSide.e2;
            }
            else {
                if (tSide.isBottomSide) {
                    tEdge = tEdge1Dist < tEdge2Dist ? tSide.e1 : tSide.e2;
                }
                else
                    tEdge = tEdge1Dist > tEdge2Dist ? tSide.e1 : tSide.e2;
            }
        }
        //hide dimension if viewed from side
        const tEdgeDir = tEdge.p2.subtract(tEdge.p1).normalize();
        const tCamDir = this.getCameraDirectionToPoint(pView, tEdge.center);
        if (Math.abs(Vector3.Dot(tCamDir, tEdgeDir)) > 0.999) { //~2°
            pDim.textNode.setEnabled(false);
            pDim.lines.setEnabled(false);
            return;
        }
        //update line
        const tOffsetEdge = this.updateDimensionLines(pDim, tSide, tEdge, pScaleFactor);
        //update text
        this.updateDimensionText(pView, pDim, tSide, tOffsetEdge, pScaleFactor);
    }
    //returns the an edge inluding the offset for the correct text position
    updateDimensionLines(pDim, pSide, pEdge, pScaleFactor) {
        const tOffsetDir = pEdge.center.subtract(pSide.center).normalize();
        const tOffsetMain = tOffsetDir.scale(2 * pScaleFactor * this.mOptions.offsetFactor);
        const tOffset1 = tOffsetDir.scale(0.5 * pScaleFactor + 2 * pScaleFactor * this.mOptions.offsetFactor);
        const tOffset2 = tOffsetDir.scale(-0.5 * pScaleFactor + 2 * pScaleFactor * this.mOptions.offsetFactor);
        const tP1 = pEdge.p1.add(tOffsetMain);
        const tP2 = pEdge.p2.add(tOffsetMain);
        CreateLineSystem("dimension_line", {
            lines: [
                [tP1, tP2],
                [pEdge.p1.add(tOffset1), pEdge.p1.add(tOffset2)],
                [pEdge.p2.add(tOffset1), pEdge.p2.add(tOffset2)] //offset line 2
            ],
            updatable: true,
            instance: pDim.lines,
            useVertexAlpha: false
        }, this.mMngr.app.model.scene);
        return ({
            p1: tP1,
            p2: tP2,
            center: Vector3.Center(tP1, tP2)
        });
    }
    updateDimensionText(pView, pDim, pSide, pEdge, pScaleFactor) {
        const tProjP1 = pView.projectPoint3(pEdge.p1, CoordinatesType.Viewer);
        const tProjP2 = pView.projectPoint3(pEdge.p2, CoordinatesType.Viewer);
        if (tProjP1 == null || tProjP2 == null) {
            pDim.textNode.setEnabled(false);
            return;
        }
        const tDirScreen = tProjP1.subtract(tProjP2).normalize();
        tProjP1.z = 0;
        pView.unprojectPointToRef(tProjP1, CoordinatesType.Viewer, null, tProjP1);
        tProjP2.z = 0;
        pView.unprojectPointToRef(tProjP2, CoordinatesType.Viewer, null, tProjP2);
        const tDirWorld = tProjP1.subtract(tProjP2).normalize();
        const tZ = pView.cameraControl.direction;
        const tX = tDirWorld;
        const tY = Vector3.Cross(tZ, tX);
        tY.normalize();
        //rotate text to camera
        quaternionFromAxesToRef(tX, tY, tZ, pDim.textNode.rotationQuaternion);
        pDim.textNode.position = pEdge.center;
        pDim.textNode.scaling.x = 7 * pScaleFactor;
        pDim.textNode.scaling.y = 7 * pScaleFactor;
        pDim.textNode.scaling.z = 7 * pScaleFactor;
        //flip text rotation if necessary
        let tAngle = signedAngleBetweenVectors2(VECTOR2_X, new Vector2(tDirScreen.x, tDirScreen.y));
        tAngle += Math.PI;
        const tDegAngle = radToDeg(tAngle);
        if (tDegAngle > 88.0 && tDegAngle < 268.0) { //2° tolerance
            pDim.textPlane.rotation.z = 0;
        }
        else {
            pDim.textPlane.rotation.z = Math.PI;
        }
        //flip text plane offset if necessary
        getLocalMatrixToRef(pDim.textNode, gTmpMatrix);
        const tAbsPos = Vector3.TransformCoordinates(pDim.textPlane.position, gTmpMatrix);
        const tDistAbsPos = Vector3.Distance(pSide.center, tAbsPos);
        const tDistTextPos = Vector3.Distance(pSide.center, pEdge.center);
        if (tDistTextPos > tDistAbsPos) {
            pDim.textPlane.position.y = -pDim.textPlane.position.y;
        }
    }
}
//// temp variables ////////////////////////////////////////////////////////////////////////////
const gTmpMatrix = Matrix.Identity();
